import { Space, Switch as AntdSwitch, SwitchProps as AntdSwitchProps, Typography } from 'antd';
import noop from 'lodash/noop';
import { ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { BodilColor, BodilFont, NorlysColor, NorlysFont } from '~src/branding';
import { useBranding } from '~src/context/BrandingContext';
import { useDisplayContext } from '~src/context/DisplayContext';

import { Circle, Input, SwitchWrapper, Track } from './style';

export type SwitchProps = Omit<AntdSwitchProps, 'checked'> & {
  value?: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  transitionTime?: number;
  style?: CSSProperties;
  yesLabel?: string;
  noLabel?: string;
  label?: string;
  checkedColor?: string;
};

const Wrapper = styled.div<{ transitionTime: number; checkedColor?: string }>(
  ({ transitionTime, checkedColor = BodilColor.ui.action.default }) => ({
    '.ant-switch-checked': {
      background: checkedColor,
    },
    '.ant-switch-handle': {
      transition: `all ${transitionTime}s ease-in-out`,
    },
  })
);

const { Text } = Typography;

export const AntSwitch = ({
  transitionTime = 0.2,
  style,
  yesLabel,
  noLabel,
  value,
  checkedColor: checkedColorInput,
  ...rest
}: SwitchProps) => {
  const { lightColor } = useBranding();
  const checkedColor = checkedColorInput ?? lightColor;

  return (
    <Wrapper {...{ style, transitionTime, checkedColor }}>
      <Space>
        {!!noLabel && <span>{noLabel}</span>}
        <AntdSwitch {...{ checked: value, ...rest }} />
        {!!yesLabel && <span>{yesLabel}</span>}
      </Space>
    </Wrapper>
  );
};

type LabelSwitchProps = Omit<SwitchProps, 'label'> & {
  label: ReactNode;
  containerStyle?: CSSProperties;
  labelStyle?: CSSProperties;
};

export const LabelSwitch = ({ label, containerStyle, labelStyle, ...rest }: LabelSwitchProps) => {
  const { isMobile } = useDisplayContext();

  return (
    <div style={{ ...containerStyle }}>
      <Space size={'middle'}>
        <AntSwitch {...rest} />
        <Text style={{ ...BodilFont.style('default', isMobile ? 14 : 12), ...labelStyle }}>{label}</Text>
      </Space>
    </div>
  );
};

export const LabelSwitchNorlys = (props: LabelSwitchProps) => {
  const { isMobile } = useDisplayContext();

  const labelStyle = { ...NorlysFont.style('default', isMobile ? 14 : 12) };

  return <LabelSwitch {...props} labelStyle={labelStyle} checkedColor={NorlysColor.secondary.purple} />;
};

export const Switch = ({
  value = false,
  disabled = false,
  onChange,
  transitionTime = 0.4,
  style,
  yesLabel,
  noLabel,
  label,
}: SwitchProps) => {
  const onClickHandler = () => {
    if (!disabled) {
      onChange(!value);
    }
  };

  const inner = (
    <SwitchWrapper>
      <Input checked={value} disabled={disabled} type='checkbox' onChange={noop} />
      <Circle transitionTime={transitionTime} />
      <Track checked={value} disabled={disabled} onClick={onClickHandler} transitionTime={transitionTime} />
    </SwitchWrapper>
  );

  return (
    <div {...{ style }}>
      {label && <div style={{ ...BodilFont.style('heading', 14), marginBottom: '8px' }}>{label}</div>}
      {noLabel && <>{noLabel}&nbsp;</>}
      {inner}
      {yesLabel && <>&nbsp;{yesLabel}</>}
    </div>
  );
};
